/**
 * Service för att hantera användarpreferenser som sparas lokalt på enheten
 */

const PREFERENCES_KEY = 'quizter:preferences';

class UserPreferencesService {
  /**
   * Hämtar sparade preferenser från localStorage
   */
  getPreferences() {
    try {
      const stored = localStorage.getItem(PREFERENCES_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('[UserPreferences] Kunde inte läsa preferenser:', error);
      return {};
    }
  }

  /**
   * Sparar preferenser till localStorage
   */
  savePreferences(preferences) {
    try {
      localStorage.setItem(PREFERENCES_KEY, JSON.stringify(preferences));
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('userPreferences:changed'));
      }
    } catch (error) {
      console.error('[UserPreferences] Kunde inte spara preferenser:', error);
    }
  }

  /**
   * Hämtar sparat alias
   */
  getAlias() {
    const prefs = this.getPreferences();
    return prefs.alias || '';
  }

  /**
   * Sparar alias
   */
  saveAlias(alias) {
    const prefs = this.getPreferences();
    prefs.alias = alias;
    this.savePreferences(prefs);
  }

  /**
   * Tar bort sparat alias
   */
  removeAlias() {
    const prefs = this.getPreferences();
    if ('alias' in prefs) {
      delete prefs.alias;
      this.savePreferences(prefs);
    }
  }

  /**
   * Hämtar sparad kontaktuppgift
   */
  getContact() {
    const prefs = this.getPreferences();
    return prefs.contact || '';
  }

  /**
   * Sparar kontaktuppgift
   */
  saveContact(contact) {
    const prefs = this.getPreferences();
    prefs.contact = contact;
    this.savePreferences(prefs);
  }

  /**
   * Tar bort sparad kontaktuppgift
   */
  removeContact() {
    const prefs = this.getPreferences();
    if ('contact' in prefs) {
      delete prefs.contact;
      this.savePreferences(prefs);
    }
  }

  /**
   * Rensar alla sparade preferenser
   */
  clearPreferences() {
    try {
      localStorage.removeItem(PREFERENCES_KEY);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('userPreferences:changed'));
      }
    } catch (error) {
      console.error('[UserPreferences] Kunde inte rensa preferenser:', error);
    }
  }

  /**
   * Om AI-hjälp/insikter ska visas
   */
  getAiHelpEnabled() {
    const prefs = this.getPreferences();
    if (prefs.aiHelpEnabled === undefined || prefs.aiHelpEnabled === null) {
      return true;
    }
    return Boolean(prefs.aiHelpEnabled);
  }

  /**
   * Spara AI-hjälp/insikter
   */
  saveAiHelpEnabled(isEnabled) {
    const prefs = this.getPreferences();
    prefs.aiHelpEnabled = Boolean(isEnabled);
    this.savePreferences(prefs);
  }

  /**
   * Hämtar senast använda AI-genereringsinställningar
   */
  getAiGenerationDefaults() {
    const prefs = this.getPreferences();
    return prefs.aiGenerationDefaults || null;
  }

  /**
   * Sparar AI-genereringsinställningar
   */
  saveAiGenerationDefaults(payload) {
    const prefs = this.getPreferences();
    prefs.aiGenerationDefaults = payload || null;
    this.savePreferences(prefs);
  }

  /**
   * Om större textläge är aktiverat
   */
  getLargeTextEnabled() {
    const prefs = this.getPreferences();
    return Boolean(prefs.largeTextEnabled);
  }

  /**
   * Spara större textläge
   */
  saveLargeTextEnabled(isEnabled) {
    const prefs = this.getPreferences();
    prefs.largeTextEnabled = Boolean(isEnabled);
    this.savePreferences(prefs);
  }

  /**
   * Om högkontrastläge är aktiverat
   */
  getHighContrastEnabled() {
    const prefs = this.getPreferences();
    return Boolean(prefs.highContrastEnabled);
  }

  /**
   * Spara högkontrastläge
   */
  saveHighContrastEnabled(isEnabled) {
    const prefs = this.getPreferences();
    prefs.highContrastEnabled = Boolean(isEnabled);
    this.savePreferences(prefs);
  }
}

export const userPreferencesService = new UserPreferencesService();
